import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_Account_FindForNotifyAll_VM } from 'ab-api/web/account/findForNotifyAll';

import type UserGroupRequestModel from 'ab-requestModels/userGroup.requestModel';

import type { RootState } from 'af-reducers';

import Breadcrumbs from 'af-components/Breadcrumbs';

import * as AccountActions from 'af-actions/accounts';
import * as CompanyActions from 'af-actions/companies';
import * as UserGroupActions from 'af-actions/userGroup';

import AutomaticNotificationsEdit from './AutomaticNotifications/Edit';
import AutomaticNotificationsPreview from './AutomaticNotifications/Preview';
import CredentialNotificationsEdit from './CredentialNotifications/Edit';
import CredentialNotificationsPreview from './CredentialNotifications/Preview';
import { GeneralNotifications, DefaultNotificationTimes, SpecialEmails, AutomaticNotifications, CompanyFeatureFlagsFM, TemporaryLaborNotificationsForm, CompanyFeatureFlagEntries } from './formModel';
import GeneralNotificationsEdit from './GeneralNotifications/Edit';
import GeneralNotificationsPreview from './GeneralNotifications/Preview';
import Loading from './Loading';
import NotificationTimesEdit from './NotificationTimes/Edit';
import NotificationTimesPreview from './NotificationTimes/Preview';
import SpecialEmailsEdit from './SpecialEmails/Edit';
import SpecialEmailsPreview from './SpecialEmails/Preview';
import TemporaryLaborNotificationsEdit from './TemporaryLaborNotifications/Edit';
import TemporaryLaborNotificationsPreview from './TemporaryLaborNotifications/Preview';

type OwnProps = CustomRouteComponentProps<void>;

type Props = OwnProps & ConnectedProps<typeof connector>;

const NotificationDetails: React.FC<Props> = (props) => {
	const {
		company,
		editGeneralNotifications,
		editDefaultNotificationSendTimes,
		editSpecialEmailNotifications,
		editAutomaticNotifications,
		editTemporaryLaborNotifications,
		editCredentialNotifications,
		getCompany,
		findAllForCompany,
		findAllForNotifyAll,
	} = props;

	const [isAutomaticNotificationsModalOpen, setIsAutomaticNotificationsModalOpen] = React.useState(false);
	const [isGeneralNotificationsModalOpen, setIsGeneralNotificationsModalOpen] = React.useState(false);
	const [isDefaultTimesModalOpen, setIsDefaultTimesModalOpen] = React.useState(false);
	const [isSpecialEmailsModalOpen, setIsSpecialEmailsModalOpen] = React.useState(false);
	const [isTemporaryLaborModalOpen, setIsTemporaryLaborModalOpen] = React.useState(false);
	const [isCredentialNotificationsModalOpen, setIsCredentialNotificationsModalOpen] = React.useState(false);
	const [companyFeatureFlagsEntries, setCompanyFeatureFlagsEntries] = React.useState<CompanyFeatureFlagEntries>({} as CompanyFeatureFlagEntries);
	const [userGroupsForCompany, setUserGroupsForCompany] = React.useState<UserGroupRequestModel[]>([]);
	const [usersForCompany, setUsersForCompany] = React.useState<W_Account_FindForNotifyAll_VM['rows']>([]);

	React.useEffect(() => {
		const fetchAndInitializeData = async () => {
			await getCompany();

			const [
				userGroups,
				users,
			] = await Promise.all([
				findAllForCompany(),
				findAllForNotifyAll(),
			]);
			setUserGroupsForCompany(userGroups);
			setUsersForCompany(users);
		};
		fetchAndInitializeData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (!company?.id) {
			return;
		}
		setCompanyFeatureFlagsEntries(new CompanyFeatureFlagEntries(CompanyFeatureFlagsFM.fromCompanyViewModel(company)));
	}, [company]);

	const toggleGeneralNotifications = React.useCallback(() => {
		setIsGeneralNotificationsModalOpen(!isGeneralNotificationsModalOpen);
	}, [isGeneralNotificationsModalOpen]);

	const toggleDefaultTimes = React.useCallback(() => {
		setIsDefaultTimesModalOpen(!isDefaultTimesModalOpen);
	}, [isDefaultTimesModalOpen]);

	const toggleSpecialEmails = React.useCallback(() => {
		setIsSpecialEmailsModalOpen(!isSpecialEmailsModalOpen);
	}, [isSpecialEmailsModalOpen]);

	const toggleAutomaticNotifications = React.useCallback(() => {
		setIsAutomaticNotificationsModalOpen(!isAutomaticNotificationsModalOpen);
	}, [isAutomaticNotificationsModalOpen]);

	const toggleCredentialNotifications = React.useCallback(() => {
		setIsCredentialNotificationsModalOpen(!isCredentialNotificationsModalOpen);
	}, [isCredentialNotificationsModalOpen]);

	const toggleTemporaryLaborNotifications = React.useCallback(() => {
		setIsTemporaryLaborModalOpen(!isTemporaryLaborModalOpen);
	}, [isTemporaryLaborModalOpen]);

	if (!company) {
		return <Loading />;
	}

	return (
		<div className="form-segment">
			<Breadcrumbs items={[{ label: 'Notifications' }]} />
			<GeneralNotificationsPreview
				company={company}
				general={companyFeatureFlagsEntries.general}
				toggleEdit={toggleGeneralNotifications}
			/>
			<GeneralNotificationsEdit
				editGeneralNotifications={editGeneralNotifications}
				initialValues={GeneralNotifications.fromCompanyViewModel(company)}
				isVisible={isGeneralNotificationsModalOpen}
				toggleEdit={toggleGeneralNotifications}
			/>

			{
				companyFeatureFlagsEntries.areNotificationsOn &&
				<>
					<Breadcrumbs items={[{ label: 'Default notification times' }]} />
					<NotificationTimesPreview
						defaultSendTimes={companyFeatureFlagsEntries.defaultSendTimes}
						toggleEdit={toggleDefaultTimes}
					/>
					<NotificationTimesEdit
						editDefaultNotificationSendTimes={editDefaultNotificationSendTimes}
						initialValues={DefaultNotificationTimes.fromCompanyViewModel(company)}
						isVisible={isDefaultTimesModalOpen}
						toggleEdit={toggleDefaultTimes}
					/>

					<Breadcrumbs items={[{ label: 'Special Emails' }]} />
					<SpecialEmailsPreview
						specialEmails={companyFeatureFlagsEntries.specialEmails}
						toggleEdit={toggleSpecialEmails}
					/>
					<SpecialEmailsEdit
						editSpecialEmailNotifications={editSpecialEmailNotifications}
						initialValues={SpecialEmails.fromCompanyViewModel(company)}
						isVisible={isSpecialEmailsModalOpen}
						toggleEdit={toggleSpecialEmails}
						userGroupsForCompany={userGroupsForCompany}
					/>

					<Breadcrumbs items={[{ label: 'Automatic Work Order notifications' }]} />
					<AutomaticNotificationsPreview
						toggleEdit={toggleAutomaticNotifications}
						workOrderNotifications={companyFeatureFlagsEntries.workOrderNotifications}
					/>
					<AutomaticNotificationsEdit
						editAutomaticNotifications={editAutomaticNotifications}
						initialValues={AutomaticNotifications.fromCompanyViewModel(company)}
						isVisible={isAutomaticNotificationsModalOpen}
						toggleEdit={toggleAutomaticNotifications}
					/>

					{companyFeatureFlagsEntries.credentials &&
						<>
							<Breadcrumbs items={[{ label: 'Employee Credential Notifications' }]} />
							<CredentialNotificationsPreview
								credentials={companyFeatureFlagsEntries.credentials}
								toggleEdit={toggleCredentialNotifications}
							/>
							<CredentialNotificationsEdit
								credentials={company.notification.companyCredentials}
								editCredentialNotifications={editCredentialNotifications}
								showModal={isCredentialNotificationsModalOpen}
								toggleEdit={toggleCredentialNotifications}
								userGroups={userGroupsForCompany}
								users={usersForCompany}
							/>
						</>
					}
					<Breadcrumbs items={[{ label: 'Temporary labor' }]} />
					<TemporaryLaborNotificationsPreview
						toggleEdit={toggleTemporaryLaborNotifications}
						workOrderNotifications={companyFeatureFlagsEntries.temporaryLaborNotifications}
					/>
					<TemporaryLaborNotificationsEdit
						editTemporaryLaborNotifications={editTemporaryLaborNotifications}
						initialValues={TemporaryLaborNotificationsForm.fromCompanyViewModel(company)}
						isVisible={isTemporaryLaborModalOpen}
						toggleEdit={toggleTemporaryLaborNotifications}
					/>
				</>
			}

		</div >
	);
};

function mapStateToProps(state: RootState) {
	const { company } = state.company;

	return {
		company,
	};
}

function mapDispatchToProps() {
	return {
		findAllForCompany: UserGroupActions.findAllForCompany,
		findAllForNotifyAll: AccountActions.findAllForNotifyAll,
		getCompany: CompanyActions.getCompany,
		editGeneralNotifications: CompanyActions.editGeneralNotifications,
		editDefaultNotificationSendTimes: CompanyActions.editDefaultNotificationSendTimes,
		editSpecialEmailNotifications: CompanyActions.editSpecialEmailNotifications,
		editAutomaticNotifications: CompanyActions.editAutomaticNotifications,
		editTemporaryLaborNotifications: CompanyActions.editTemporaryLaborNotifications,
		editCredentialNotifications: CompanyActions.editCredentialNotificationSettings,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType>(
	React.memo,
	connector
);

export default enhance(NotificationDetails);
